'use client';

import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './ShopScreen.translations';
import { Button, BottomSheet } from '../../components/ui';
import { GetShopSkinsSortBy } from '../../services/generated';
import { FaChevronDown } from '../../components/icons';
import { cn } from '../../utils/cn';

export interface SkinsFilterValues {
  rarity: string;
  sortBy: string;
}

export const DEFAULT_SKINS_FILTERS: SkinsFilterValues = {
  rarity: '',
  sortBy: GetShopSkinsSortBy.PriceAsc,
};

const MiniSelect: FC<{ value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = options.find(o => o.value === value)?.label || options[0]?.label || '';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        className="flex items-center justify-between w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-base text-white/80 cursor-pointer transition-colors hover:bg-white/[0.07] outline-none text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{selectedLabel}</span>
        <FaChevronDown className={cn('text-[11px] text-white/30 shrink-0 ml-2 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0c0f16] border border-white/[0.08] rounded-xl overflow-hidden z-50 shadow-[0_8px_24px_rgba(0,0,0,0.6)] max-h-[260px] overflow-y-auto [scrollbar-width:none]">
          {options.map(opt => (
            <button
              key={opt.value}
              className={cn(
                'flex items-center w-full px-3.5 py-2.5 text-base border-none cursor-pointer transition-colors text-left',
                opt.value === value ? 'bg-white/[0.08] text-white' : 'bg-transparent text-white/50 hover:bg-white/[0.04] hover:text-white/80',
              )}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RARITIES = [
  { value: '', label: 'all' },
  { value: 'Consumer Grade', label: 'Consumer Grade' },
  { value: 'Industrial Grade', label: 'Industrial Grade' },
  { value: 'Mil-Spec Grade', label: 'Mil-Spec Grade' },
  { value: 'Restricted', label: 'Restricted' },
  { value: 'Classified', label: 'Classified' },
  { value: 'Covert', label: 'Covert' },
];

interface SkinsFilterModalProps {
  isOpen: boolean;
  filters: SkinsFilterValues;
  onApply: (filters: SkinsFilterValues) => void;
  onClose: () => void;
}

export const SkinsFilterModal: FC<SkinsFilterModalProps> = ({ isOpen, filters, onApply, onClose }) => {
  const { t } = useLocalTranslation(translations);
  const [local, setLocal] = useState<SkinsFilterValues>(filters);

  useEffect(() => {
    if (isOpen) setLocal(filters);
  }, [isOpen, filters.rarity, filters.sortBy]);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('showFilters')} heightClass="h-auto max-h-[85vh]">
      <div className="flex flex-col gap-4 pt-1">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-white/45">{t('filtersRarity')}</label>
          <MiniSelect
            value={local.rarity}
            options={RARITIES.map(r => ({ value: r.value, label: r.label === 'all' ? t('filtersAllRarities') : r.label }))}
            onChange={(rarity) => setLocal({ ...local, rarity })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-white/45">{t('filtersSortBy')}</label>
          <MiniSelect
            value={local.sortBy}
            options={[
              { value: GetShopSkinsSortBy.PriceAsc, label: t('filtersSortByPriceAsc') },
              { value: GetShopSkinsSortBy.PriceDesc, label: t('filtersSortByPriceDesc') },
            ]}
            onChange={(sortBy) => setLocal({ ...local, sortBy })}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={() => setLocal(DEFAULT_SKINS_FILTERS)} stretched size="l">
            {t('filtersReset')}
          </Button>
          <Button onClick={() => { onApply(local); onClose(); }} stretched size="l">
            {t('filtersApply')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};
